/**
 * DuplicateActionModal — Zentraler "Was tun mit diesem Konflikt?"-Dialog.
 *
 * Wird geöffnet, wenn der User in der Tabelle auf den roten Warn-Button in
 * der rowNumber-Zelle klickt. Anders als der DuplicateMergeModal ist dieser
 * hier ein Entscheidungs-Dialog VOR dem Merge:
 *
 *   - zeigt alle beteiligten Kontakte (Anchor + others) mit Kern-Feldern
 *   - stellt drei Aktionen zur Auswahl:
 *       1. Zusammenführen  → öffnet DuplicateMergeModal für alle Kontakte
 *       2. Einen Kontakt entfernen  → soft-delete via mergeContactPair auf
 *          den anderen (verlustfrei, Daten wandern mit), keine Zerstörung
 *       3. Kein Duplikat  → dismisst den Alert (reversibel)
 *
 * Design-Prinzipien:
 *   - Kein blindes Löschen: "Entfernen" ist ein 1-Schritt-Merge in den
 *     verbleibenden Kontakt, damit keine Historie/Notiz/Send verloren geht.
 *   - Klare Optionsliste statt einem Wall-of-buttons.
 *   - Overlay + zentrierter Card, konsistent zu MergeModal.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import './DuplicateActionModal.css';

// Reihenfolge = Priorität in der Karte. Der Header (Firma) wird separat
// gerendert; die E-Mail bekommt eine eigene Kollisions-Zeile darüber. Der
// Kontakt-Status wird als farbiger Badge separat gerendert (nicht als
// Detail-Row) — er ist mit Abstand die wichtigste Entscheidungshilfe:
// "wurde dieser Kontakt schon angeschrieben, in welchem Schritt?"
const DETAIL_FIELDS = [
  { key: 'lead_website',   label: 'Website' },
  { key: 'lead_telephone', label: 'Phone' },
  { key: 'lead_country',   label: 'Country' },
  { key: 'lead_city',      label: 'City' },
  { key: 'lead_industry',  label: 'Industry' },
];

// Farb/Icon-Mapping spiegelt NewWorldDbStatusFilter.js, damit der Status im
// Modal genauso aussieht wie in der Tabellen-Filter-Leiste — konsistente
// visuelle Sprache über die WorldDash hinweg.
const STATUS_STYLES = {
  'Not Contacted':     { color: '#94a3b8', icon: 'fa-circle',            label: 'Not Contacted',    sent: false },
  'Lead':              { color: '#3b82f6', icon: 'fa-bullseye',          label: 'Lead',             sent: false },
  'In Outreach':       { color: '#6366f1', icon: 'fa-satellite-dish',    label: 'In Outreach',      sent: false },
  'Introduced':        { color: '#8b5cf6', icon: 'fa-paper-plane',       label: 'Introduced',       sent: true  },
  'First Follow-up':   { color: '#a78bfa', icon: 'fa-reply',             label: '1st Follow-Up',    sent: true  },
  'Second Follow-up':  { color: '#c4b5fd', icon: 'fa-reply-all',         label: '2nd Follow-Up',    sent: true  },
  'Third Follow-up':   { color: '#ddd6fe', icon: 'fa-forward',           label: '3rd Follow-Up',    sent: true  },
  'replied':           { color: '#10b981', icon: 'fa-comment-dots',      label: 'Replied',          sent: true  },
  'Interested':        { color: '#22c55e', icon: 'fa-circle-check',      label: 'Interested',       sent: true  },
  'Not Interested':    { color: '#ef4444', icon: 'fa-circle-xmark',      label: 'Not Interested',   sent: true },
  'Outreach: Removed': { color: '#f59e0b', icon: 'fa-user-minus',        label: 'Outreach Removed', sent: false },
  'Outreach: Failed':  { color: '#ef4444', icon: 'fa-exclamation-triangle', label: 'Outreach Failed', sent: false },
};
function getStatusStyle(raw) {
  if (!raw) return STATUS_STYLES['Not Contacted'];
  return STATUS_STYLES[raw] || { color: '#64748b', icon: 'fa-tag', label: String(raw), sent: false };
}

function hasVal(v) {
  if (v == null) return false;
  if (typeof v === 'string') return v.trim() !== '' && v.trim() !== '-';
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v).length > 0;
  return true;
}
function hexToRgba(hex, alpha) {
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) return `rgba(148,163,184,${alpha})`;
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const num = parseInt(full, 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

function formatRelative(msOrIso) {
  if (!msOrIso) return '';
  const t = typeof msOrIso === 'string' ? Date.parse(msOrIso) : Number(msOrIso);
  if (!t || Number.isNaN(t)) return '';
  const diff = Date.now() - t;
  const day = 86400000;
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.round(diff / 60000)} min ago`;
  if (diff < day) return `${Math.round(diff / 3600000)} h ago`;
  if (diff < 7 * day) return `${Math.round(diff / day)} days ago`;
  return new Date(t).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function DuplicateActionModal({
  userId,
  alert,           // duplicate-alert-Doc aus useDuplicateAlerts
  onClose,
  onOpenContact,   // (contactId) => void
  onStartMerge,    // (contactIds[]) => void  → parent öffnet MergeModal
  embedded = false,
}) {
  const contactIds = useMemo(() => {
    if (!alert) return [];
    const list = [alert.currentContactId, ...(Array.isArray(alert.otherContactIds) ? alert.otherContactIds : [])];
    return Array.from(new Set(list.filter(Boolean)));
  }, [alert]);

  const [contacts, setContacts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [removingId, setRemovingId] = useState(null); // id während des Merge-Calls
  const [confirmRemoveId, setConfirmRemoveId] = useState(null); // id im Confirm-Zustand

  useEffect(() => {
    if (!alert || contactIds.length < 2) return undefined;
    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        const db = getFirestore();
        const snaps = await Promise.all(
          contactIds.map((id) => getDoc(doc(db, `World/${userId}/db/${id}`))),
        );
        if (cancel) return;
        const map = {};
        snaps.forEach((s, i) => {
          if (s.exists()) map[contactIds[i]] = { id: s.id, ...s.data() };
        });
        setContacts(map);
      } catch (err) {
        if (!cancel) setError(err && err.message ? err.message : String(err));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, contactIds.join('|'), alert?.id]);

  const activeContacts = contactIds
    .map((id) => contacts[id])
    .filter((c) => c && !c.merged_into);

  // Entfernen = 1-Schritt-Merge in den (ersten anderen) verbleibenden Kontakt.
  // Nichts wird zerstört, alle Daten wandern mit. Bei 3+ Kontakten wandert der
  // "Entfernte" in den ersten anderen — der User kann danach die weiteren
  // Duplikate mit "Zusammenführen" gebündelt behandeln.
  const handleRemoveContact = async (loserId) => {
    if (!loserId || activeContacts.length < 2) return;
    const winner = activeContacts.find((c) => c.id !== loserId);
    if (!winner) return;
    setBusy(true);
    setRemovingId(loserId);
    try {
      const call = httpsCallable(functions, 'mergeContactPair');
      const res = await call({
        userId,
        winnerId: winner.id,
        loserId,
        alertId: alert.id,
      });
      if (!res.data || !res.data.ok) throw new Error('remove-merge failed');
      // Beim letzten verbleibenden Konflikt ist der Alert vom Server auf
      // 'merged' gesetzt und die Karte verschwindet über den Live-Feed.
      // Bei 3+ Kontakten schließen wir nicht — die anderen Karten sollen
      // dem User noch die weiteren Optionen zeigen.
      if (activeContacts.length <= 2) {
        onClose && onClose();
      } else {
        // lokal die entfernte Karte ausblenden — der Snapshot-Refresh vom
        // Server folgt gleich mit.
        setContacts((prev) => {
          const next = { ...prev };
          if (next[loserId]) next[loserId] = { ...next[loserId], merged_into: winner.id };
          return next;
        });
        setConfirmRemoveId(null);
      }
    } catch (err) {
      console.error('[DuplicateActionModal] remove failed:', err);
      setError(err && err.message ? err.message : 'Remove failed');
    } finally {
      setBusy(false);
      setRemovingId(null);
    }
  };

  const handleMerge = () => {
    onStartMerge?.(activeContacts.map((c) => c.id), contacts);
    if (!embedded) onClose && onClose();
  };

  if (!alert) return null;

  const sheet = (
      <div
        className="dup-action dup-action-sheet"
        onClick={embedded ? undefined : (e) => e.stopPropagation()}
      >
        <div className="dup-action-header">
          <div className="dup-action-headerCopy">
            <h2 className="dup-action-title">Duplicate Conflict</h2>
            <p className="dup-action-subtitle">
              {alert.emailInQuestion
                ? <>The email <code className="dup-action-email">{alert.emailInQuestion}</code> exists on multiple contacts.</>
                : 'This contact collides with others in your database.'}
            </p>
          </div>
          {!busy && (
            <button type="button" onClick={onClose} className="dup-action-close" aria-label="Close">×</button>
          )}
        </div>

        {loading && (
          <div className="dup-action-contacts" aria-busy="true" aria-live="polite">
            {Array.from({ length: Math.max(contactIds.length, 2) }).map((_, i) => (
              <ContactCardSkeleton key={`skel-${i}`} delay={i * 90} />
            ))}
          </div>
        )}

        {!loading && error && (
          <div className="dup-action-center dup-action-center--error">{error}</div>
        )}

        {!loading && !error && (
          <div className="dup-action-contacts">
            {activeContacts.map((c) => {
              const others = activeContacts.filter((x) => x.id !== c.id);
              const targetWinner = others[0];
              return (
                <ContactCard
                  key={c.id}
                  contact={c}
                  collisionEmail={alert.emailInQuestion || ''}
                  canRemove={activeContacts.length >= 2}
                  isConfirming={confirmRemoveId === c.id}
                  isRemoving={removingId === c.id}
                  removeTargetName={targetWinner ? (targetWinner.business_name || '(no name)') : ''}
                  disabled={busy && removingId !== c.id}
                  onOpen={() => onOpenContact && onOpenContact(c.id)}
                  onAskRemove={() => setConfirmRemoveId(c.id)}
                  onCancelRemove={() => setConfirmRemoveId(null)}
                  onConfirmRemove={() => handleRemoveContact(c.id)}
                />
              );
            })}
          </div>
        )}

        <div className="dup-action-footer">
          <button
            type="button"
            className="dup-action-merge"
            onClick={handleMerge}
            disabled={busy || loading || !!error || activeContacts.length < 2}
          >
            <span>
              <span className="dup-action-mergeTitle">Merge contacts</span>
              <span className="dup-action-mergeDesc">
                Combine all data, history and activities into one contact. Recommended when it's the same company.
              </span>
            </span>
          </button>
        </div>
      </div>
  );

  if (embedded) return sheet;

  const node = (
    <div className="dup-action-overlay" onClick={busy ? undefined : onClose}>
      <div className="dup-overlay-slot">{sheet}</div>
    </div>
  );

  if (typeof document === 'undefined') return node;
  return createPortal(node, document.body);
}

function ContactCardSkeleton({ delay = 0 }) {
  return (
    <div className="dup-action-card dup-action-card--skel" style={{ animationDelay: `${delay}ms` }}>
      <div className="dup-skel dup-skel--title" />
      <div className="dup-skel dup-skel--pill" />
      <div className="dup-skel dup-skel--email" />
      <div className="dup-action-skelDetails">
        <div className="dup-skel dup-skel--line" />
        <div className="dup-skel dup-skel--line dup-skel--lineShort" />
      </div>
      <div className="dup-action-skelActions">
        <div className="dup-skel dup-skel--btn" />
        <div className="dup-skel dup-skel--btn" />
      </div>
    </div>
  );
}

function ContactCard({
  contact, collisionEmail,
  canRemove, isConfirming, isRemoving, removeTargetName, disabled,
  onOpen, onAskRemove, onCancelRemove, onConfirmRemove,
}) {
  const emails = String(contact.lead_email || '')
    .split(/[,;]/).map((s) => s.trim()).filter(Boolean);
  const collisionLc = String(collisionEmail || '').toLowerCase();
  const primaryEmail = emails.find((e) => e.toLowerCase() === collisionLc) || emails[0] || '';
  const extraEmailCount = Math.max(emails.length - 1, 0);

  const detailRows = DETAIL_FIELDS
    .map((f) => ({ ...f, value: contact[f.key] }))
    .filter((f) => hasVal(f.value));

  const statusStyle = getStatusStyle(contact.lead_contacted);
  const wasContacted = statusStyle.sent || !!contact.lead_contacted_timestamp;

  const notesCount = Array.isArray(contact.lead_notes) ? contact.lead_notes.length : 0;
  const dmCount = (() => {
    const raw = contact.lead_decision_makers;
    if (Array.isArray(raw)) return raw.length;
    if (typeof raw === 'string' && raw.trim() && raw.trim() !== '[]') {
      try { const p = JSON.parse(raw); return Array.isArray(p) ? p.length : 0; } catch { return 0; }
    }
    return 0;
  })();
  const createdRel = formatRelative(contact.timestamp || contact.created_at);
  const contactedRel = formatRelative(contact.lead_contacted_timestamp);
  const hasReplied = contact.lead_reply === true;
  const hasPrepared = contact.has_prepared_email === true;

  return (
    <div className={`dup-action-card${wasContacted ? ' dup-action-card--contacted' : ''}`}>
      <div className="dup-action-cardHead">
        <div className="dup-action-cardName" title={contact.business_name || ''}>
          {contact.business_name || '(no name)'}
        </div>
        {wasContacted && (
          <span className="dup-action-sent" title="Already contacted">
            <i className="fa fa-paper-plane" aria-hidden />
            Contacted
          </span>
        )}
      </div>

      <div
        className="dup-action-status"
        style={{
          color: statusStyle.color,
          background: hexToRgba(statusStyle.color, 0.09),
          border: `1px solid ${hexToRgba(statusStyle.color, 0.24)}`,
        }}
      >
        <i className={`fa ${statusStyle.icon}`} aria-hidden />
        <span>{statusStyle.label}</span>
      </div>

      {primaryEmail && (
        <div
          className={`dup-action-chip${primaryEmail.toLowerCase() === collisionLc ? ' dup-action-chip--hit' : ''}`}
          title={primaryEmail}
        >
          <i className="fa fa-envelope" aria-hidden />
          <span className="dup-action-chipText">{primaryEmail}</span>
          {extraEmailCount > 0 && (
            <span className="dup-action-chipMore">+{extraEmailCount}</span>
          )}
        </div>
      )}

      {(detailRows.length > 0) && (
        <div className="dup-action-details">
          {detailRows.map((f) => (
            <div key={f.key} className="dup-action-detail">
              <span className="dup-action-detailLabel">{f.label}</span>
              <span className="dup-action-detailValue">{String(f.value)}</span>
            </div>
          ))}
        </div>
      )}

      {(createdRel || contactedRel || notesCount > 0 || dmCount > 0 || hasReplied || hasPrepared) && (
        <div className="dup-action-meta">
          {createdRel && (
            <span className="dup-action-pill" title="Created">
              <i className="fa fa-plus" aria-hidden />{createdRel}
            </span>
          )}
          {contactedRel && (
            <span className="dup-action-pill" title="Last contacted">
              <i className="fa fa-paper-plane" aria-hidden />{contactedRel}
            </span>
          )}
          {notesCount > 0 && (
            <span className="dup-action-pill" title="Notes">
              <i className="fa fa-sticky-note" aria-hidden />{notesCount}
            </span>
          )}
          {dmCount > 0 && (
            <span className="dup-action-pill" title="Decision makers">
              <i className="fa fa-users" aria-hidden />{dmCount}
            </span>
          )}
          {hasReplied && (
            <span className="dup-action-pill dup-action-pill--hot" title="Has replied">
              <i className="fa fa-reply" aria-hidden />replied
            </span>
          )}
          {hasPrepared && (
            <span className="dup-action-pill dup-action-pill--warn" title="Prepared email pending">
              <i className="fa fa-hourglass-half" aria-hidden />prepared
            </span>
          )}
        </div>
      )}

      {detailRows.length === 0 && !createdRel && !contactedRel && notesCount === 0 && (
        <div className="dup-action-empty">No additional details available</div>
      )}

      {isConfirming ? (
        <div className="dup-action-confirm">
          <div className="dup-action-confirmText">
            Remove this contact? All data, notes and activities will be moved to{' '}
            <strong>{removeTargetName || 'the remaining contact'}</strong>.
          </div>
          <div className="dup-action-confirmActions">
            <button
              type="button"
              onClick={onCancelRemove}
              disabled={isRemoving}
              className="dup-action-btn dup-action-btn--ghost"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirmRemove}
              disabled={isRemoving}
              className="dup-action-btn dup-action-btn--danger"
            >
              {isRemoving ? 'Removing…' : 'Yes, remove'}
            </button>
          </div>
        </div>
      ) : (
        <div className="dup-action-cardActions">
          {onOpen && (
            <button
              type="button"
              onClick={onOpen}
              disabled={disabled}
              className="dup-action-btn dup-action-btn--open"
              title="Jump to contact in table"
            >
              <i className="fa fa-arrow-up-right-from-square" aria-hidden />
              Open
            </button>
          )}
          {canRemove && onAskRemove && (
            <button
              type="button"
              onClick={onAskRemove}
              disabled={disabled}
              className="dup-action-btn dup-action-btn--remove"
              title="Remove this contact — data is transferred losslessly to the other"
            >
              <i className="fa fa-trash" aria-hidden />
              Remove
            </button>
          )}
        </div>
      )}
    </div>
  );
}
